import { useTranslate } from '@growtherapy/i18n';
import { Button, Link, cn } from '@growtherapy/sprout-ui';

import { getAppConfig } from '../config';
import { useTheme } from '../hooks';
import { Translate } from '../i18n/Translate';
import { Theme } from '../state';
import { useContactUs } from '../useContactUs';

export type ProviderNotHereDescriptionProps = {
  providerIsOnline: boolean | null;
  clientHasAppointment: boolean;
  appointmentHasProviderNoShowReport: boolean;
  isPastNoShowThreshold: boolean;
  clientJoinedAfterWindowToReport: boolean;
  recordProviderNoShowAndRedirect: () => void;
  onOpenChatDrawer: () => void;
  onClose: () => void;
};

export function ProviderNotHereDescription({
  providerIsOnline,
  clientHasAppointment,
  appointmentHasProviderNoShowReport,
  isPastNoShowThreshold,
  clientJoinedAfterWindowToReport,
  recordProviderNoShowAndRedirect,
  onOpenChatDrawer,
  onClose,
}: ProviderNotHereDescriptionProps) {
  const t = useTranslate();
  const marketplaceOrigin = getAppConfig().marketplaceOrigin;
  const { pageUrl } = useContactUs();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  const supportText = (
    <Translate
      i18nKey='telehealth-core:providerNotHere.thereIsNoSessionScheduledForThisTime'
      components={{
        1: (
          <Link
            href={pageUrl}
            size='md'
            className={cn('text-neutral-800 hover:text-neutral-900', {
              'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                isDarkTheme,
            })}
          />
        ),
      }}
    />
  );

  if (providerIsOnline) {
    return (
      <Translate
        i18nKey='telehealth-core:providerNotHere.yourProviderAppearsToBeOnlineButHasntAdmittedYou'
        components={{
          1: (
            <Button
              use='link'
              onClick={onOpenChatDrawer}
              className={cn('text-neutral-800 hover:text-neutral-900', {
                'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                  isDarkTheme,
              })}
            />
          ),
        }}
      />
    );
  }

  if (!clientHasAppointment) {
    return supportText;
  }
  if (appointmentHasProviderNoShowReport) {
    return (
      <Translate
        i18nKey='telehealth-core:providerNotHere.weReceivedAConfirmationThatYourProviderDidntShowUp'
        components={{
          1: (
            <Link
              external
              className={cn('text-neutral-800 font-medium hover:text-neutral-900', {
                'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                  isDarkTheme,
              })}
              href={`${marketplaceOrigin}/filters?utm_source=telehealth-booking`}
            />
          ),
        }}
      />
    );
  }

  if (clientJoinedAfterWindowToReport) {
    return <>{t('telehealth-core:providerNotHere.theGracePeriodForTheSessionHasPassed')}</>;
  }

  return (
    <>
      {t('telehealth-core:providerNotHere.yourProviderHasA10MinuteGracePeriod')}
      <Button
        disabled={!isPastNoShowThreshold}
        className='w-full mt-3'
        aria-live='polite'
        onClick={() => {
          recordProviderNoShowAndRedirect();
          onClose();
        }}
      >
        {isPastNoShowThreshold && <span className='sr-only'>{t('telehealth-core:providerNotHere.buttonEnabled')}</span>}
        {t('telehealth-core:providerNotHere.myProviderIsNotHere')}
      </Button>
    </>
  );
}
